export type GaEventName =
  | "study_planner_started"
  | "study_plan_generated"
  | "calendar_invite_downloaded"
  | "sign_up"
  | "login"
  | "join_study_group"
  | "create_study_group"
  | "click_app_download"
  | "content_created"
  | "content_engaged";

export type GaEventParams = Record<string, string | number | boolean | undefined>;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

/** Set by NextAuth on a successful sign-in. Client reads it once and sends `login`. */
export const GA_LOGIN_COOKIE = "cl_ga_login";
/** First-party flag so later visits from a staff browser are left out of GA4. */
export const GA_INTERNAL_COOKIE = "cl_ga_internal";
const SIGNUP_COOKIE = "cl_ga_signup";
const SKIP_LOGIN_UNTIL_KEY = "ga_skip_login_until";

const ATTRIBUTION_PARAMS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "utm_id",
  "gclid",
  "gbraid",
  "wbraid",
] as const;

/** Path + UTM/ad params only — drops reset tokens, invite codes, etc. */
export function gaPageLocation(): string {
  if (typeof window === "undefined") return "";
  const kept = new URLSearchParams();
  const params = new URLSearchParams(window.location.search);
  for (const key of ATTRIBUTION_PARAMS) {
    const value = params.get(key);
    if (value) kept.set(key, value);
  }
  const query = kept.toString();
  return `${window.location.origin}${window.location.pathname}${query ? `?${query}` : ""}`;
}

export function gaCampaignParams(): GaEventParams | undefined {
  if (typeof window === "undefined") return undefined;
  const params = new URLSearchParams(window.location.search);
  const campaign: GaEventParams = {};
  const source = params.get("utm_source");
  const medium = params.get("utm_medium");
  const name = params.get("utm_campaign");
  const term = params.get("utm_term");
  const content = params.get("utm_content");
  if (source) campaign.campaign_source = source;
  if (medium) campaign.campaign_medium = medium;
  if (name) campaign.campaign_name = name;
  if (term) campaign.campaign_term = term;
  if (content) campaign.campaign_content = content;
  return Object.keys(campaign).length ? campaign : undefined;
}

export function isInternalGaUser(
  user: { role?: string | null; roles?: string[] | null; isSeedAdmin?: boolean | null } | null | undefined,
): boolean {
  if (!user) return false;
  if (user.isSeedAdmin) return true;
  const roles = [user.role, ...(user.roles || [])]
    .filter((role): role is string => typeof role === "string" && role.length > 0)
    .map((role) => role.toUpperCase());
  return roles.includes("ADMIN") || roles.includes("EMPLOYEE");
}

export function isGaInternal(): boolean {
  if (typeof document === "undefined") return false;
  return document.cookie.split("; ").some((part) => part.startsWith(`${GA_INTERNAL_COOKIE}=1`));
}

/** Remember this browser as internal team traffic for a year. */
export function markInternalTraffic(): void {
  if (typeof document === "undefined" || isGaInternal()) return;
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${GA_INTERNAL_COOKIE}=1; Max-Age=${365 * 24 * 60 * 60}; Path=/; SameSite=Lax${secure}`;
}

/**
 * Account creation that immediately signs the person in should count as
 * sign_up only. Call this before that sign-in so the following login hit is dropped.
 */
export function suppressFollowingLoginEvent(): void {
  try {
    sessionStorage.setItem(SKIP_LOGIN_UNTIL_KEY, String(Date.now() + 60_000));
  } catch {
    /* private mode */
  }
}

export function clearLoginSuppression(): void {
  try {
    sessionStorage.removeItem(SKIP_LOGIN_UNTIL_KEY);
  } catch {
    /* private mode */
  }
}

export function takeLoginSuppression(): boolean {
  try {
    const raw = sessionStorage.getItem(SKIP_LOGIN_UNTIL_KEY);
    if (!raw) return false;
    sessionStorage.removeItem(SKIP_LOGIN_UNTIL_KEY);
    return Date.now() < Number(raw);
  } catch {
    return false;
  }
}

export function trackGaEvent(name: GaEventName, params?: GaEventParams): void {
  if (typeof window === "undefined" || isGaInternal()) return;
  const cleaned: Record<string, string | number | boolean> = {};
  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined) cleaned[key] = value;
    }
  }
  if (typeof window.gtag === "function") {
    window.gtag("event", name, cleaned);
    return;
  }
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(["event", name, cleaned]);
}

const ADS_REG_CONV_STORAGE = "cl_ads_reg_conv";
const adsRegConvFired = new Set<string>();
let adsTagLoad: Promise<void> | null = null;

function ensureAdsGtag(): Promise<void> {
  if (typeof window === "undefined") return Promise.resolve();
  if (typeof window.gtag === "function") return Promise.resolve();
  if (adsTagLoad) return adsTagLoad;
  adsTagLoad = new Promise((resolve) => {
    window.dataLayer = window.dataLayer || [];
    window.gtag = (...args: unknown[]) => {
      window.dataLayer!.push(args);
    };
    window.gtag("js", new Date());
    window.gtag("config", "AW-18426426561");
    const script = document.createElement("script");
    script.async = true;
    script.src = "https://www.googletagmanager.com/gtag/js?id=AW-18426426561";
    script.onload = () => resolve();
    script.onerror = () => resolve();
    document.head.appendChild(script);
  });
  return adsTagLoad;
}

/**
 * Fire Google Ads "Beta Registration Complete" once per new account.
 * Dedupes across React re-renders, route changes, and page refreshes.
 * Only call this after the backend confirms a brand-new User was created.
 */
export function fireGoogleAdsRegistrationConversion(userId?: string | null): boolean {
  if (typeof window === "undefined" || isGaInternal()) return false;
  const key = userId || "claimed";
  if (adsRegConvFired.has(key) || adsRegConvFired.has("claimed")) return false;
  try {
    const stored = sessionStorage.getItem(ADS_REG_CONV_STORAGE);
    if (stored) return false;
  } catch {
    /* private mode */
  }

  adsRegConvFired.add(key);
  try {
    sessionStorage.setItem(ADS_REG_CONV_STORAGE, key);
  } catch {
    /* private mode */
  }

  const payload = { send_to: "AW-18426426561/qGmcCNLshu0cEMHps9JE" };
  const send = () => {
    if (typeof window.gtag === "function") {
      window.gtag("event", "conversion", payload);
      return;
    }
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(["event", "conversion", payload]);
  };

  if (typeof window.gtag === "function") {
    send();
    return true;
  }
  void ensureAdsGtag().then(send);
  return true;
}

/** One-time cookie set by NextAuth createUser after a new Google account. */
export function consumeAdsRegistrationConversionCookie(): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(/(?:^|; )cl_ads_reg_conv=([^;]*)/);
  if (!match) return null;
  document.cookie = "cl_ads_reg_conv=; Max-Age=0; path=/";
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}

/** Set by NextAuth jwt after a successful sign-in to an existing account. */
export function consumeGaLoginCookie(): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(new RegExp(`(?:^|; )${GA_LOGIN_COOKIE}=([^;]*)`));
  if (!match) return null;
  document.cookie = `${GA_LOGIN_COOKIE}=; Max-Age=0; path=/`;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}

/** Set by NextAuth createUser for first-time Google sign-ups. */
export function consumeGaSignupCookie(): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(new RegExp(`(?:^|; )${SIGNUP_COOKIE}=([^;]*)`));
  if (!match) return null;
  document.cookie = `${SIGNUP_COOKIE}=; Max-Age=0; path=/`;
  return decodeURIComponent(match[1]);
}

function requestPath(input: RequestInfo | URL): string {
  try {
    const href =
      typeof input === "string"
        ? input
        : input instanceof URL
          ? input.href
          : input.url;
    return new URL(href, window.location.origin).pathname;
  } catch {
    return "";
  }
}

function requestMethod(input: RequestInfo | URL, init?: RequestInit): string {
  if (init?.method) return init.method.toUpperCase();
  if (typeof Request !== "undefined" && input instanceof Request) {
    return input.method.toUpperCase();
  }
  return "GET";
}

function approvedJoinAction(init?: RequestInit): boolean {
  const body = init?.body;
  if (typeof body !== "string") return false;
  try {
    const parsed = JSON.parse(body) as { action?: string };
    return parsed.action === "approve";
  } catch {
    return false;
  }
}

function studyGroupAction(path: string): "create" | "join" | null {
  if (path === "/api/study-groups/create") return "create";
  if (path === "/api/study-groups/join") return "join";
  if (/^\/api\/study-groups\/[^/]+\/(?:join|claim)$/.test(path)) return "join";
  return null;
}

function createdContentType(path: string): string | null {
  if (path === "/api/feed/posts") return "post";
  if (path === "/api/forums/topics") return "forum_topic";
  if (path === "/api/milestones") return "milestone";
  return null;
}

function engagedAction(path: string): string | null {
  if (path.endsWith("/celebrate")) return "celebrate";
  if (path.endsWith("/like")) return "like";
  if (path.endsWith("/comments")) return "comment";
  if (path.endsWith("/bookmark")) return "bookmark";
  if (path.endsWith("/share")) return "share";
  return null;
}

/**
 * Watches successful POSTs so content_created / content_engaged fire on every
 * surface (feed, profile, hashtag, forums) without duplicating handlers.
 */
export function installGaApiTracking(): () => void {
  if (typeof window === "undefined" || (window.fetch as { __gaPatched?: boolean }).__gaPatched) {
    return () => {};
  }

  const originalFetch = window.fetch.bind(window);
  const patched: typeof window.fetch & { __gaPatched?: boolean } = (input, init) => {
    const method = requestMethod(input, init);
    const path = requestPath(input);
    return originalFetch(input, init).then((response) => {
      if (method === "POST" && response.ok) {
        const groupAction = studyGroupAction(path);
        if (groupAction === "create") {
          trackGaEvent("create_study_group");
        } else if (groupAction === "join") {
          void response
            .clone()
            .json()
            .then((data) => {
              if (data && typeof data === "object" && "joinRequest" in data && data.joinRequest) return;
              trackGaEvent("join_study_group", { method: "direct" });
            })
            .catch(() => {
              trackGaEvent("join_study_group", { method: "direct" });
            });
        }
        const contentType = createdContentType(path);
        if (contentType) {
          trackGaEvent("content_created", { content_type: contentType });
        } else if (!groupAction) {
          const action = engagedAction(path);
          if (action) trackGaEvent("content_engaged", { engagement_type: action });
        }
      } else if (
        method === "PATCH" &&
        response.ok &&
        /^\/api\/study-groups\/[^/]+\/join-requests$/.test(path) &&
        approvedJoinAction(init)
      ) {
        trackGaEvent("join_study_group", { method: "approved" });
      }
      return response;
    });
  };
  patched.__gaPatched = true;
  window.fetch = patched;
  return () => {
    window.fetch = originalFetch;
  };
}
